/**
 * Edición inline de existencias: UNA edición = UNA petición.
 *
 * Bug de prod (14-oct, insumos): Enter confirmaba la edición, el input se deshabilitaba al
 * guardar, perdía el foco y onBlur la confirmaba OTRA vez con el mismo delta (dos "+9 Ajuste ·
 * Edición directa" en el mismo segundo). La sesión de edición se confirma una sola vez y lleva
 * su llave de idempotencia (el backend descarta una segunda petición con la misma llave).
 */
export interface EditSession {
  /** Llave de idempotencia de ESTA edición (va al backend como adjustmentKey). */
  readonly key: string;
  /** Ejecuta `fn` solo la primera vez (Enter, blur o ambos). Devuelve si la ejecutó. */
  commit(fn: (key: string) => void): boolean;
  /** Escape: la edición termina sin guardar; un blur posterior ya no confirma nada. */
  cancel(): void;
}

export function newEditSession(): EditSession {
  const key = crypto.randomUUID();
  let done = false;
  return {
    key,
    commit(fn) { if (done) return false; done = true; fn(key); return true; },
    cancel() { done = true; },
  };
}

// ── Existencia por unidad (solo visualización; el valor guardado no cambia) ──
const unitOf = (u?: string | null) => (u ?? '').trim().toLowerCase();

/** Valor redondeado como se muestra: g y ml sin decimales; kg y L con 3; pza sin decimales salvo
 *  que la fracción sea real (entonces hasta 3). */
export function roundQtyForUnit(v: number | string | null | undefined, unit?: string | null): number {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return 0;
  const u = unitOf(unit);
  if (u === 'g' || u === 'ml') return Math.round(n);
  if (u === 'pza' && Math.abs(n - Math.round(n)) < 1e-6) return Math.round(n);
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}

/** Texto de la existencia: "1,250 g", "2.500 kg", "12 pza", "2.5 pza". Sin la unidad. */
export function fmtInsumoQty(v: number | string | null | undefined, unit?: string | null): string {
  const u = unitOf(unit);
  const r = roundQtyForUnit(v, unit);
  if (u === 'g' || u === 'ml') return r.toLocaleString('es-MX', { maximumFractionDigits: 0 });
  if (u === 'kg' || u === 'l') return r.toLocaleString('es-MX', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return r.toLocaleString('es-MX', { maximumFractionDigits: 3 });
}
